import posthog from 'posthog-js';
import { preparePostHogCapture } from '@/lib/analytics/posthog-capture-policy';
import { partnerFromHost } from '@/features/partner/partnerHosts';

// PostHog runs on PROD ONLY: NEXT_PUBLIC_POSTHOG_KEY exists solely in the
// Production Vercel env (staging's key removed 2026-08-19 — staging must send
// nothing). Gating on the key — not VERCEL_ENV — because NEXT_PUBLIC_VERCEL_ENV
// does not reliably inline at build on the prod Vercel build, which silently
// disabled all browser analytics on prod.
// The local games playground (/dev/games) is dev tooling and never initializes analytics.
// Partner hosts (Freecroco) never initialize it either: their players are not Quizball users.
if (
  typeof window !== 'undefined'
  && process.env.NEXT_PUBLIC_POSTHOG_KEY
  && !/^\/dev\/games(\/|$)/.test(window.location.pathname)
  && !partnerFromHost(window.location.host)
) {
  try {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
      person_profiles: 'identified_only',
      // Initial load + pathname-changing SPA navigations (query/hash-only changes
      // don't fire). Re-enabled 2026-08-19 (owner
      // call) for acquisition/referrer visibility — costs ~20k events/day,
      // which is why it was off; prod-only since staging has no key.
      capture_pageview: 'history_change',
      // $pageleave on every navigation — redundant with our explicit funnel
      // events, so off to cut event volume.
      capture_pageleave: false,
      // Autocapture fires a $autocapture on every click/submit/change. In a
      // tap-heavy game that's huge event volume and fully redundant with our
      // ~60 named events — disabled to cut the bill.
      autocapture: false,
      // Do not fetch flags during normal app startup. Experiment helpers
      // temporarily unpause + reload only after application-level eligibility
      // is known. This preserves the request-cost guard instead of restoring a
      // /flags request for every app visit.
      advanced_disable_feature_flags: false,
      advanced_disable_feature_flags_on_first_load: true,
      // Session replays are a major event/ingest cost driver and are not part of
      // our core analytics (we rely on ~60 named funnel events). Disabled to cut
      // the bill; re-enable temporarily if a specific debugging/abuse
      // investigation needs replays.
      disable_session_recording: true,
      // Search Console has too little traffic for field Core Web Vitals data.
      // Capture one batched $web_vitals event on public football quiz pages so
      // mobile LCP, CLS and INP can be optimized from real visits. Network
      // timings stay off; before_send drops vitals from the high-volume game.
      capture_performance: {
        network_timing: false,
        web_vitals: true,
        web_vitals_allowed_metrics: ['LCP', 'CLS', 'FCP', 'INP'],
      },
      before_send: preparePostHogCapture,
    });
    // identify() normally reloads flags automatically. Keep those reloads
    // paused globally; experiment helpers own their narrow manual reloads.
    posthog.featureFlags.setReloadingPaused(true);
  } catch (error) {
    console.error('PostHog initialization error:', error);
  }
}
